import {ref, computed} from "vue";

// apiResult unwraps the { success, data, message } returned by useExecuteApi.
// execute still returns the complete result and rethrows exceptions.
export function useAsync(asyncFunction = null, {apiResult = false} = {}) {
  const result = ref(null);
  const data = ref(null);
  const error = ref(null);
  const isLoading = ref(false);

  const isSuccess = computed(() => !isLoading.value && !error.value && (apiResult ? result.value?.success === true : data.value !== null));

  const isError = computed(() => !isLoading.value && error.value !== null);
  const errorMessage = computed(() => error.value?.message ?? "");

  const execute = async (...args) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await asyncFunction?.(...args);

      result.value = response;
      data.value = apiResult ? response?.data ?? null : response;

      if (apiResult && response?.success === false) {
        error.value = response;
      }

      return response;
    } catch (err) {
      result.value = null;
      if (apiResult) data.value = null;
      error.value = err;

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const reset = () => {
    result.value = null;
    data.value = null;
    error.value = null;
    isLoading.value = false;
  };

  return {
    result,       // Full function result; in API mode: useExecuteApi's { success, data, message, ... }
    data,         // API mode: only useExecuteApi's data; otherwise: the full function result
    error,        // Failed useExecuteApi result (success: false), or an exception thrown by the function
    errorMessage, // Message from the failed useExecuteApi result or thrown exception; otherwise ''
    isLoading,    // True while useAsync is executing the function
    isSuccess,    // True after loading, without errors: API mode checks success; default mode checks data
    isError,      // True after loading if an API failure or thrown exception was recorded
    execute,      // Execute the function; return the full useExecuteApi result in API mode
    reset,        // Clear stored results, data, errors, and loading state; does not cancel requests
  };
}
